import { clamp, midiToFrequency } from "../../core/math";
import { scheduleAttack, scheduleRelease, smoothSet, type Adsr } from "../envelope";
import { DEFAULT_MAX_VOICES, HARD_MAX_VOICES, type Instrument } from "./types";

export type Waveform = "sine" | "triangle" | "sawtooth" | "square";

export interface SynthParams extends Adsr {
  waveform: Waveform;
  osc2Waveform: Waveform;
  osc2Mix: number; // 0–1
  osc2DetuneCents: number; // -1200..1200
  cutoffHz: number; // 40–18000
  resonance: number; // Q 0.1–20
  volume: number; // 0–1 instrument level
  maxVoices: number;
}

/** Vibrato at full mod wheel (± cents) and rate. */
export const MAX_VIBRATO_CENTS = 50;
export const VIBRATO_HZ = 5.5;

export const DEFAULT_SYNTH_PARAMS: SynthParams = {
  waveform: "sawtooth", osc2Waveform: "square", osc2Mix: 0.3, osc2DetuneCents: 7,
  attackSec: 0.01, decaySec: 0.3, sustain: 0.6, releaseSec: 0.4,
  cutoffHz: 2400, resonance: 1, volume: 0.7, maxVoices: DEFAULT_MAX_VOICES,
};

interface Voice {
  note: number;
  vib: GainNode; // scales the shared LFO to this note's frequency (Hz)
  osc1: OscillatorNode;
  osc2: OscillatorNode;
  osc2Gain: GainNode;
  filter: BiquadFilterNode;
  amp: GainNode;
  peak: number;
  startSec: number;
  released: boolean;
  endSec: number;
}

/** Two-oscillator subtractive synthesizer built from native nodes. */
export class SynthInstrument implements Instrument<SynthParams> {
  readonly kind = "synth";
  readonly output: GainNode;
  private _params: SynthParams;
  private voices: Voice[] = [];
  private disposed = false;
  private bendCents = 0;
  /**
   * One shared vibrato LFO. `lfoDepth` (0–1, the mod wheel) feeds a per-voice
   * gain sized in Hz for that note, which modulates oscillator *frequency*.
   * Frequency rather than `detune` because some Web Audio implementations
   * (observed: node-web-audio-api 2.2) ignore signals connected to `detune`.
   */
  private lfo: OscillatorNode;
  private lfoDepth: GainNode;

  constructor(private ctx: BaseAudioContext, params: Partial<SynthParams> = {}) {
    this._params = { ...DEFAULT_SYNTH_PARAMS, ...params };
    this.output = ctx.createGain();
    this.output.gain.value = this._params.volume;
    this.lfo = ctx.createOscillator();
    this.lfo.frequency.value = VIBRATO_HZ;
    this.lfoDepth = ctx.createGain();
    this.lfoDepth.gain.value = 0;
    this.lfo.connect(this.lfoDepth);
    this.lfo.start(ctx.currentTime);
  }

  get params(): SynthParams { return this._params; }
  get activeVoices(): number { return this.voices.length; }

  noteOn(note: number, velocity: number, timeSec = this.ctx.currentTime): void {
    if (this.disposed) return;
    const p = this._params;
    // Retrigger: release an unreleased voice on the same note.
    for (const v of this.voices) if (v.note === note && !v.released) this.release(v, timeSec);
    const cap = clamp(Math.round(p.maxVoices), 1, HARD_MAX_VOICES);
    while (this.voices.length >= cap) this.kill(this.voices[0]!, timeSec); // steal oldest

    const freq = midiToFrequency(note);
    const osc1 = this.ctx.createOscillator();
    osc1.type = p.waveform;
    osc1.frequency.value = freq;
    osc1.detune.value = this.bendCents;
    const osc2 = this.ctx.createOscillator();
    osc2.type = p.osc2Waveform;
    osc2.frequency.value = freq;
    osc2.detune.value = p.osc2DetuneCents + this.bendCents;
    // ±MAX_VIBRATO_CENTS expressed in Hz around this note (linear approximation, < 1 cent error).
    const vib = this.ctx.createGain();
    vib.gain.value = freq * (Math.pow(2, MAX_VIBRATO_CENTS / 1200) - 1);
    this.lfoDepth.connect(vib);
    vib.connect(osc1.frequency);
    vib.connect(osc2.frequency);
    const osc2Gain = this.ctx.createGain();
    osc2Gain.gain.value = p.osc2Mix;
    const filter = this.ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = p.cutoffHz;
    filter.Q.value = p.resonance;
    const amp = this.ctx.createGain();
    amp.gain.value = 0;
    const mix = 1 / (1 + p.osc2Mix); // keep level stable as osc2 is mixed in
    const peak = clamp(velocity, 0, 1) * 0.5 * mix;

    osc1.connect(filter);
    osc2.connect(osc2Gain).connect(filter);
    filter.connect(amp).connect(this.output);
    scheduleAttack(amp.gain, p, peak, timeSec);
    osc1.start(timeSec);
    osc2.start(timeSec);

    const voice: Voice = { note, vib, osc1, osc2, osc2Gain, filter, amp, peak, startSec: timeSec, released: false, endSec: Infinity };
    osc1.onended = () => this.cleanup(voice);
    this.voices.push(voice);
  }

  noteOff(note: number, timeSec = this.ctx.currentTime): void {
    for (const v of this.voices) if (v.note === note && !v.released) this.release(v, timeSec);
  }

  releaseAll(timeSec = this.ctx.currentTime): void {
    for (const v of this.voices) if (!v.released) this.release(v, timeSec);
  }

  hardStop(): void {
    const now = this.ctx.currentTime;
    for (const v of [...this.voices]) this.kill(v, now);
  }

  setParams(params: Partial<SynthParams>): void {
    this._params = { ...this._params, ...params };
    const p = this._params;
    if (params.volume !== undefined) smoothSet(this.output.gain, p.volume, this.ctx);
    // Live-update sounding voices for continuous params; envelope/waveform apply to new notes.
    for (const v of this.voices) {
      if (params.cutoffHz !== undefined) smoothSet(v.filter.frequency, p.cutoffHz, this.ctx);
      if (params.resonance !== undefined) smoothSet(v.filter.Q, p.resonance, this.ctx);
      if (params.osc2DetuneCents !== undefined) smoothSet(v.osc2.detune, p.osc2DetuneCents + this.bendCents, this.ctx);
      if (params.osc2Mix !== undefined) smoothSet(v.osc2Gain.gain, p.osc2Mix, this.ctx);
    }
  }

  pitchBend(semitones: number): void {
    this.bendCents = clamp(semitones, -24, 24) * 100;
    for (const v of this.voices) {
      smoothSet(v.osc1.detune, this.bendCents, this.ctx, 0.005);
      smoothSet(v.osc2.detune, this._params.osc2DetuneCents + this.bendCents, this.ctx, 0.005);
    }
  }

  modulation(amount: number): void {
    smoothSet(this.lfoDepth.gain, clamp(amount, 0, 1), this.ctx, 0.02);
  }

  dispose(): void {
    this.hardStop();
    for (const v of [...this.voices]) this.cleanup(v);
    try { this.lfo.stop(); } catch { /* already stopped */ }
    this.lfo.disconnect();
    this.lfoDepth.disconnect();
    this.output.disconnect();
    this.disposed = true;
  }

  private release(v: Voice, timeSec: number): void {
    v.released = true;
    const at = Math.max(timeSec, v.startSec);
    v.endSec = scheduleRelease(v.amp.gain, this._params, v.peak, v.startSec, at);
    v.osc1.stop(v.endSec);
    v.osc2.stop(v.endSec);
  }

  /** Fast 5 ms fade and stop (voice stealing, panic). */
  private kill(v: Voice, timeSec: number): void {
    v.released = true;
    v.amp.gain.cancelScheduledValues(timeSec);
    v.amp.gain.setTargetAtTime(0, timeSec, 0.005);
    v.endSec = timeSec + 0.03;
    try { v.osc1.stop(v.endSec); v.osc2.stop(v.endSec); } catch { /* already stopped */ }
    // Remove from the active list immediately so the cap is respected.
    this.voices = this.voices.filter((x) => x !== v);
    setTimeoutSafe(() => this.cleanup(v), 60);
  }

  private cleanup(v: Voice): void {
    this.voices = this.voices.filter((x) => x !== v);
    try { this.lfoDepth.disconnect(v.vib); } catch { /* already disconnected */ }
    v.vib.disconnect();
    for (const n of [v.osc1, v.osc2, v.osc2Gain, v.filter, v.amp]) n.disconnect();
  }
}

/** setTimeout that tolerates environments without timers (offline rendering tests). */
export function setTimeoutSafe(fn: () => void, ms: number): void {
  if (typeof setTimeout === "function") setTimeout(fn, ms);
}
